import { useState, useRef, useCallback } from 'react';
import { PhysicsObject, ShapeType } from '@/types/simulation';
import { useTheme } from '@/contexts/ThemeContext';

interface Props {
  objects: PhysicsObject[];
  selectedObjectId: string | null;
  onSelect: (id: string) => void;
  onUpdate: (id: string, updates: Partial<PhysicsObject>) => void;
  onAdd: () => void;
  onRemove: (id: string) => void;
  objectCollisions: boolean;
  onToggleCollisions: () => void;
}

// ---- Smart numeric input ----
// フォーカス中: 生テキスト（.00なし）で自由編集
// フォーカス外: 小数点2桁で整形表示
interface NumericInputProps {
  value: number;
  step?: number;
  label: string;
  accentColor?: string;
  isDark?: boolean;
  onChange: (v: number) => void;
}

function NumericInput({ value, step = 0.5, label, accentColor, isDark = true, onChange }: NumericInputProps) {
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const textDim = isDark ? 'text-white/30' : 'text-gray-400';
  const textPrimary = isDark ? 'text-white' : 'text-gray-900';

  // フォーカス時: 末尾の余分な0を除去した文字列にする
  const handleFocus = useCallback(() => {
    // 整数なら整数表示、小数なら末尾0を除去
    const raw = Number.isInteger(value)
      ? String(value)
      : parseFloat(value.toFixed(6)).toString();
    setDraft(raw);
    setFocused(true);
    // 全選択で上書きしやすく
    setTimeout(() => inputRef.current?.select(), 0);
  }, [value]);

  const handleBlur = useCallback(() => {
    setFocused(false);
    const parsed = parseFloat(draft);
    if (!isNaN(parsed)) onChange(parsed);
    setDraft('');
  }, [draft, onChange]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setDraft(e.target.value);
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      inputRef.current?.blur();
    }
    if (e.key === 'Escape') {
      setDraft(String(value));
      setFocused(false);
      inputRef.current?.blur();
    }
    // 矢印キーでステップ増減
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      const cur = parseFloat(draft) || value;
      const next = Math.round((cur + step) * 1e9) / 1e9;
      setDraft(parseFloat(next.toFixed(6)).toString());
      onChange(next);
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const cur = parseFloat(draft) || value;
      const next = Math.round((cur - step) * 1e9) / 1e9;
      setDraft(parseFloat(next.toFixed(6)).toString());
      onChange(next);
    }
  }, [draft, value, step, onChange]);

  // 表示値: フォーカス中はdraft、それ以外は整形済み
  const displayValue = focused
    ? draft
    : (Number.isInteger(value) ? String(value) : value.toFixed(2));

  return (
    <div className="flex flex-col gap-1">
      <label className={`text-xs font-medium ${textDim} select-none`}>{label}</label>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          inputMode="decimal"
          value={displayValue}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          className={`glass-input w-full text-sm font-mono px-3 py-2.5 rounded-xl transition-all duration-150 ${textPrimary} outline-none`}
          style={focused && accentColor ? { borderColor: accentColor + '80', boxShadow: `0 0 0 2px ${accentColor}20` } : {}}
        />
        {/* 単位インジケーター（フォーカス時は非表示） */}
        {!focused && accentColor && (
          <div
            className="absolute right-2.5 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full opacity-60 pointer-events-none"
            style={{ backgroundColor: accentColor }}
          />
        )}
      </div>
    </div>
  );
}

// ---- Slider row ----
function SliderRow({
  label, value, min, max, step, unit = '',
  accentClass = 'accent-orange-400', textAccent = 'text-orange-400',
  isDark = true, onChange,
}: {
  label: string; value: number; min: number; max: number; step: number;
  unit?: string; accentClass?: string; textAccent?: string; isDark?: boolean;
  onChange: (v: number) => void;
}) {
  const textMuted = isDark ? 'text-white/40' : 'text-gray-500';
  return (
    <div className="space-y-1.5">
      <div className="flex justify-between items-center">
        <span className={`text-xs ${textMuted}`}>{label}</span>
        <span className={`text-xs font-mono font-semibold ${textAccent}`}>
          {value.toFixed(step < 1 ? 2 : 0)}{unit}
        </span>
      </div>
      <input
        type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className={`w-full h-1.5 rounded-full appearance-none ${accentClass} cursor-pointer`}
      />
    </div>
  );
}

const SHAPES: { type: ShapeType; icon: string; label: string }[] = [
  { type: 'circle',   icon: 'ri-circle-line',   label: '球' },
  { type: 'square',   icon: 'ri-shape-line',    label: '箱' },
  { type: 'triangle', icon: 'ri-triangle-line', label: '三角' },
];

export default function ObjectSettingsSection({
  objects, selectedObjectId, onSelect, onUpdate, onAdd, onRemove,
  objectCollisions, onToggleCollisions,
}: Props) {
  const selected = objects.find((o) => o.id === selectedObjectId) ?? null;
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const textMuted     = isDark ? 'text-white/35' : 'text-gray-500';
  const textSecondary = isDark ? 'text-white/60' : 'text-gray-600';
  const textPrimary   = isDark ? 'text-white'    : 'text-gray-900';
  const textDim       = isDark ? 'text-white/15' : 'text-gray-300';

  return (
    <div className="space-y-4 animate-float-in">

      {/* Header row */}
      <div className="flex items-center justify-between">
        <h2 className="section-label flex items-center gap-2">
          <div className="w-4 h-4 flex items-center justify-center">
            <i className={`ri-planet-line ${isDark ? 'text-orange-400' : 'text-orange-500'}`} />
          </div>
          物体
        </h2>
        <div className="flex items-center gap-2">
          {/* 物体衝突トグル */}
          <button
            onClick={onToggleCollisions}
            className={`ripple-btn flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
              objectCollisions
                ? `glass-btn-active ${isDark ? 'text-orange-300' : 'text-orange-500'}`
                : `glass-btn ${textMuted} hover:${textSecondary}`
            }`}
            title={objectCollisions ? '物体衝突: ON' : '物体衝突: OFF'}
          >
            <div className="w-3 h-3 flex items-center justify-center">
              <i className="ri-collage-line" />
            </div>
            衝突
          </button>
          <button
            onClick={onAdd}
            className={`glass-btn ripple-btn text-xs ${isDark ? 'text-emerald-300' : 'text-emerald-600'} px-3 py-1.5 rounded-lg cursor-pointer whitespace-nowrap flex items-center gap-1.5 hover:${textPrimary} transition-all`}
          >
            <div className="w-3 h-3 flex items-center justify-center">
              <i className="ri-add-line" />
            </div>
            追加
          </button>
        </div>
      </div>

      {/* Object list */}
      {objects.length === 0 ? (
        <div className="glass rounded-xl py-8 text-center">
          <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2">
            <i className={`ri-planet-line text-3xl ${textDim}`} />
          </div>
          <p className={`text-xs ${textDim} leading-relaxed`}>
            「物体を追加」で<br />新しい物体を作成
          </p>
        </div>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {objects.map((obj, idx) => (
            <div key={obj.id} className="flex items-center gap-0.5">
              <button
                onClick={() => onSelect(obj.id)}
                className={`ripple-btn flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                  selectedObjectId === obj.id
                    ? `glass-btn-active ${textPrimary}`
                    : `glass-btn ${textMuted} hover:${textSecondary}`
                }`}
              >
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: obj.color }} />
                #{idx + 1}
              </button>
              <button
                onClick={() => onRemove(obj.id)}
                className={`w-5 h-5 flex items-center justify-center ${textDim} hover:text-red-400 transition-colors cursor-pointer`}
              >
                <i className="ri-close-line text-xs" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Selected object settings */}
      {selected && (
        <div className="space-y-4 animate-float-in">

          {/* Shape picker */}
          <div>
            <p className={`text-xs ${textMuted} mb-2`}>形状</p>
            <div className="flex gap-2">
              {SHAPES.map(({ type, icon, label }) => (
                <button
                  key={type}
                  onClick={() => onUpdate(selected.id, { shape: type })}
                  className={`ripple-btn flex-1 flex flex-col items-center gap-1.5 py-2.5 rounded-xl text-xs transition-all cursor-pointer whitespace-nowrap ${
                    selected.shape === type
                      ? `glass-btn-active ${textPrimary}`
                      : `glass-btn ${textMuted} hover:${textSecondary}`
                  }`}
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`${icon} text-base`} />
                  </div>
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Physical properties */}
          <div className="glass rounded-xl p-3 space-y-3">
            <div className="flex items-center justify-between">
              <p className={`text-xs ${textMuted} flex items-center gap-1.5`}>
                <div className="w-3 h-3 flex items-center justify-center">
                  <i className={`ri-settings-3-line ${isDark ? 'text-orange-400' : 'text-orange-500'}`} />
                </div>
                物理プロパティ
              </p>
              <button
                onClick={() => onUpdate(selected.id, { size: 0.75, mass: 1.0, restitution: 0.6, friction: 0.3 })}
                className={`ripple-btn text-xs ${textDim} hover:text-red-400 transition-colors cursor-pointer flex items-center gap-1 px-2 py-0.5 rounded-md glass-btn`}
                title="デフォルトに戻す"
              >
                <div className="w-3 h-3 flex items-center justify-center">
                  <i className="ri-refresh-line text-xs" />
                </div>
                リセット
              </button>
            </div>
            <SliderRow
              label="サイズ"    value={selected.size}        min={0.05}  max={8}  step={0.05}    unit=""
              accentClass="accent-orange-400" textAccent={isDark ? 'text-orange-400' : 'text-orange-500'}
              isDark={isDark}   onChange={(v) => onUpdate(selected.id, { size: v })}
            />
            <SliderRow
              label="質量"     value={selected.mass}         min={0.1} max={10}  step={0.1}  unit=" kg"
              accentClass="accent-yellow-400" textAccent={isDark ? 'text-yellow-400' : 'text-yellow-600'}
              isDark={isDark}   onChange={(v) => onUpdate(selected.id, { mass: v })}
            />
            <SliderRow
              label="反発係数" value={selected.restitution}  min={0}   max={1}   step={0.01}
              accentClass="accent-cyan-400"   textAccent={isDark ? 'text-cyan-400' : 'text-sky-600'}
              isDark={isDark}   onChange={(v) => onUpdate(selected.id, { restitution: v })}
            />
            <SliderRow
              label="摩擦係数" value={selected.friction}     min={0}   max={1}   step={0.01}
              accentClass="accent-pink-400"   textAccent={isDark ? 'text-pink-400' : 'text-pink-500'}
              isDark={isDark}   onChange={(v) => onUpdate(selected.id, { friction: v })}
            />
          </div>

          {/* Initial velocity */}
          <div className="glass rounded-xl p-3 space-y-3">
            <div className="flex items-center justify-between">
              <p className={`text-xs ${textMuted} flex items-center gap-1.5`}>
                <div className="w-3 h-3 flex items-center justify-center">
                  <i className={`ri-arrow-right-up-line ${isDark ? 'text-cyan-400' : 'text-sky-600'}`} />
                </div>
                初期速度
              </p>
              <button
                onClick={() => onUpdate(selected.id, { vx: 0, vy: 0 })}
                className={`ripple-btn text-xs ${textDim} hover:text-red-400 transition-colors cursor-pointer flex items-center gap-1 px-2 py-0.5 rounded-md glass-btn`}
                title="速度をゼロに"
              >
                <div className="w-3 h-3 flex items-center justify-center">
                  <i className="ri-refresh-line text-xs" />
                </div>
                リセット
              </button>
            </div>

            <div className="grid grid-cols-2 gap-2.5">
              <NumericInput
                value={selected.vx}
                step={0.5}
                label="Vx（→）"
                accentColor={isDark ? '#22d3ee' : '#0284c7'}
                isDark={isDark}
                onChange={(v) => onUpdate(selected.id, { vx: v })}
              />
              <NumericInput
                value={selected.vy}
                step={0.5}
                label="Vy（↑）"
                accentColor={isDark ? '#22d3ee' : '#0284c7'}
                isDark={isDark}
                onChange={(v) => onUpdate(selected.id, { vy: v })}
              />
            </div>

            {/* Direction visualizer */}
            <div className="flex items-center justify-center pt-1">
              <div className="relative w-16 h-16">
                <div className={`absolute inset-0 rounded-full border ${isDark ? 'border-white/10' : 'border-black/10'} flex items-center justify-center`}>
                  <div className={`w-1 h-1 rounded-full ${isDark ? 'bg-white/30' : 'bg-black/25'}`} />
                </div>
                {(Math.abs(selected.vx) > 0.01 || Math.abs(selected.vy) > 0.01) && (
                  <div
                    className="absolute inset-0 flex items-center justify-center"
                    style={{ transform: `rotate(${-Math.atan2(selected.vy, selected.vx) * (180 / Math.PI)}deg)` }}
                  >
                    <div className="w-6 h-0.5 ml-2 rounded-full"
                      style={{ backgroundColor: selected.color, boxShadow: `0 0 4px ${selected.color}` }} />
                    <div className="w-0 h-0 border-t-4 border-b-4 border-l-4 border-t-transparent border-b-transparent"
                      style={{ borderLeftColor: selected.color }} />
                  </div>
                )}
                <div className={`absolute -bottom-4 left-1/2 -translate-x-1/2 text-xs ${textDim} font-mono whitespace-nowrap`}>
                  {Math.sqrt(selected.vx ** 2 + selected.vy ** 2).toFixed(1)} m/s
                </div>
              </div>
            </div>
          </div>

          {/* Position */}
          <div className="glass rounded-xl p-3 space-y-3">
            <div className="flex items-center justify-between">
              <p className={`text-xs ${textMuted} flex items-center gap-1.5`}>
                <div className="w-3 h-3 flex items-center justify-center">
                  <i className={`ri-map-pin-line ${isDark ? 'text-purple-400' : 'text-violet-600'}`} />
                </div>
                初期位置
              </p>
              <button
                onClick={() => onUpdate(selected.id, { x: 0, y: 6 })}
                className={`ripple-btn text-xs ${textDim} hover:text-red-400 transition-colors cursor-pointer flex items-center gap-1 px-2 py-0.5 rounded-md glass-btn`}
                title="原点付近に戻す"
              >
                <div className="w-3 h-3 flex items-center justify-center">
                  <i className="ri-refresh-line text-xs" />
                </div>
                リセット
              </button>
            </div>

            <div className="grid grid-cols-2 gap-2.5">
              <NumericInput
                value={selected.x}
                step={0.5}
                label="X"
                accentColor={isDark ? '#c084fc' : '#7c3aed'}
                isDark={isDark}
                onChange={(v) => onUpdate(selected.id, { x: v })}
              />
              <NumericInput
                value={selected.y}
                step={0.5}
                label="Y"
                accentColor={isDark ? '#c084fc' : '#7c3aed'}
                isDark={isDark}
                onChange={(v) => onUpdate(selected.id, { y: v })}
              />
            </div>
          </div>

        </div>
      )}
    </div>
  );
}
